import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    showIndirectlyEngagedStatistics: { type: Boolean, default: true }
  }

  static targets = [
    'totalVisitors',
    'passingVisitors',
    'engagedVisitors',
    'engagedDwell',
    'indirectlyEngagedVisitors',
    'indirectlyEngagedDwell',
    'dwell',
    'impressions',
    'passersby',
    'engagedSection',
    'indirectlyEngagedSection',
    'passingSection',
    'sensorComparisonSection',
    'dwellSection',
    'returnedVisitors',
    'returnedEngagedVisitors'
  ]

  declare readonly dwellTarget: HTMLElement
  declare readonly engagedDwellTarget: HTMLElement
  declare readonly engagedVisitorsTarget: HTMLElement
  declare readonly indirectlyEngagedDwellTarget: HTMLElement
  declare readonly indirectlyEngagedVisitorsTarget: HTMLElement
  declare readonly passingVisitorsTarget: HTMLElement
  declare readonly totalVisitorsTarget: HTMLElement
  declare readonly impressionsTarget: HTMLElement
  declare readonly passersbyTarget: HTMLElement
  declare readonly engagedSectionTarget: HTMLElement
  declare readonly indirectlyEngagedSectionTarget: HTMLElement
  declare readonly passingSectionTarget: HTMLElement
  declare readonly sensorComparisonSectionTarget: HTMLElement
  declare readonly dwellSectionTarget: HTMLElement
  declare readonly returnedVisitorsTarget: HTMLElement
  declare readonly returnedEngagedVisitorsTarget: HTMLElement

  declare readonly hasTotalVisitorsTarget: boolean
  declare readonly hasDwellTarget: boolean
  declare readonly hasEngagedDwellTarget: boolean
  declare readonly hasEngagedVisitorsTarget: boolean
  declare readonly hasIndirectlyEngagedVisitorsTarget: boolean
  declare readonly hasIndirectlyEngagedDwellTarget: boolean
  declare readonly hasPassingVisitorsTarget: boolean
  declare readonly hasImpressionsTarget: boolean
  declare readonly hasPassersbyTarget: boolean
  declare readonly hasEngagedSectionTarget: boolean
  declare readonly hasIndirectlyEngagedSectionTarget: boolean
  declare readonly hasPassingSectionTarget: boolean
  declare readonly hasSensorComparisonSectionTarget: boolean
  declare readonly hasDwellSectionTarget: boolean
  declare readonly hasReturnedVisitorsTarget: boolean
  declare readonly hasReturnedEngagedVisitorsTarget: boolean
  declare readonly showIndirectlyEngagedStatisticsValue: boolean

  setHeroStatistics ({ detail }: any): void {
    const {
      heroStatistics,
      targetEnableEngagement,
      targetEnablePassing
    } = detail

    const isEngagementEnabled = targetEnableEngagement === undefined || targetEnableEngagement === true || targetEnableEngagement === 'true'
    const isPassingEnabled = targetEnableEngagement === undefined || targetEnablePassing === true || targetEnablePassing === 'true'

    if (this.hasTotalVisitorsTarget) {
      this.totalVisitorsTarget.innerHTML = this.formatStringNumber(heroStatistics.visitors_count)
    }
    if (this.hasDwellTarget) {
      this.dwellTarget.innerHTML = this.formatSeconds(heroStatistics.dwell)
    }
    if (this.hasEngagedDwellTarget) {
      this.engagedDwellTarget.innerHTML = this.formatSeconds(heroStatistics.engaged_dwell)
    }
    if (this.hasEngagedVisitorsTarget) {
      this.engagedVisitorsTarget.innerHTML = this.formatStringNumber(heroStatistics.engaged_visitors_count)
    }
    if (this.hasIndirectlyEngagedDwellTarget && this.showIndirectlyEngagedStatisticsValue) {
      this.indirectlyEngagedDwellTarget.innerHTML = this.formatSeconds(heroStatistics.indirectly_engaged_dwell)
    }
    if (this.hasIndirectlyEngagedVisitorsTarget && this.showIndirectlyEngagedStatisticsValue) {
      this.indirectlyEngagedVisitorsTarget.innerHTML = this.formatStringNumber(heroStatistics.indirectly_engaged_visitors_count)
    }
    if (this.hasPassingVisitorsTarget) {
      this.passingVisitorsTarget.innerHTML = this.formatStringNumber(heroStatistics.passing_visitors_count)
    }
    if (this.hasImpressionsTarget) {
      this.impressionsTarget.innerHTML = this.formatStringNumber(heroStatistics.impressions)
    }
    if (this.hasPassersbyTarget) {
      this.passersbyTarget.innerHTML = this.formatStringNumber(heroStatistics.passersby)
    }
    if (this.hasReturnedVisitorsTarget) {
      this.returnedVisitorsTarget.innerHTML = this.formatStringNumber(heroStatistics.returned_visitors_count)
    }
    if (this.hasReturnedEngagedVisitorsTarget) {
      this.returnedEngagedVisitorsTarget.innerHTML = this.formatStringNumber(heroStatistics.returned_engaged_visitors_count)
    }
    if (this.hasEngagedSectionTarget) {
      this.toggleSectionChildren(this.engagedSectionTarget, !isEngagementEnabled)
    }
    if (this.hasIndirectlyEngagedSectionTarget) {
      this.toggleSectionChildren(this.indirectlyEngagedSectionTarget, !isEngagementEnabled)
    }
    if (this.hasPassingSectionTarget) {
      this.toggleSectionChildren(this.passingSectionTarget, !isPassingEnabled)
    }
    if (this.hasDwellSectionTarget) {
      this.toggleSectionChildren(this.dwellSectionTarget, !isEngagementEnabled)
    }
    if (this.hasSensorComparisonSectionTarget) {
      this.toggleSectionChildren(this.sensorComparisonSectionTarget, !isEngagementEnabled)
    }
    if (this.hasReturnedVisitorsTarget) {
      this.toggleSectionChildren(this.returnedVisitorsTarget, !isEngagementEnabled)
    }
    if (this.hasReturnedEngagedVisitorsTarget) {
      this.toggleSectionChildren(this.returnedEngagedVisitorsTarget, !isEngagementEnabled)
    }
  }

  toggleSectionChildren (section: HTMLElement, hide: boolean): void {
    Array.from(section.children).forEach((el) => {
      ;(el as HTMLElement).style.display = hide ? 'none' : ''
    })
  }

  formatSeconds (seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const formattedHours = String(hours).padStart(2, '0')
    const formattedMinutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')
    const formattedSeconds = String(seconds % 60).padStart(2, '0')

    return hours > 0
      ? `${formattedHours}h ${formattedMinutes}m ${formattedSeconds}s`
      : `${formattedMinutes}m ${formattedSeconds}s`
  }

  formatStringNumber (stringNumber: string): string {
    const num = Math.max(0, parseInt(stringNumber))
    return num.toLocaleString('en-GB')
  }
}
